// Luxury crossfade carousel — used across every gallery on the site
// (Gold Foil Art · Sculpted Accents · Gilded Glassware · Icons of Faith ·
// Bespoke Gifts · The Dubai Series). Handles:
//   • Auto-rotation (5s) with all sensible pause conditions
//   • Prominent circular gold arrows with hover-glow + focus ring
//   • One-time "beckon" nudge on the right arrow when the deck enters view
//   • Keyboard, ARIA, reduced-motion, and PDF-export compatibility
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Vitrine from "@/components/Vitrine";
import { IS_EXPORT } from "@/lib/exportMode";

const DEFAULT_AUTOPLAY_INTERVAL_MS = 5000;
const RESUME_AFTER_INTERACT_MS = 10000;

const normalize = (entry, fallbackMode, fallbackCleanOptions) => {
  if (typeof entry === "string") {
    return { src: entry, mode: fallbackMode, cleanOptions: fallbackCleanOptions, caption: null };
  }
  return {
    src: entry.src,
    mode: entry.mode || fallbackMode,
    cleanOptions: entry.cleanOptions || fallbackCleanOptions,
    caption: entry.caption || null,
  };
};

export default function VitrineCarousel({
  images,
  alt = "",
  mode = "bleed",
  aspect = "aspect-[3/4]",
  delay = 0,
  cleanOptions,
  intervalMs = DEFAULT_AUTOPLAY_INTERVAL_MS,
}) {
  const list = (Array.isArray(images) ? images : [images]).map((e) =>
    normalize(e, mode, cleanOptions),
  );
  const [index, setIndex] = useState(0);
  const total = list.length;
  const canAutoplay = total > 1 && !IS_EXPORT;

  // Refs & state that gate the timer
  const rootRef = useRef(null);
  const interactPauseTimerRef = useRef(null);
  const autoplayTimerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(
    typeof document !== "undefined" ? document.hidden : false,
  );
  const [pausedByInteract, setPausedByInteract] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [beckonPlayed, setBeckonPlayed] = useState(false);

  // In PDF export, render ONLY the visible (active) image so file size stays small
  const visibleList = IS_EXPORT ? list.slice(0, 1) : list;
  const activeCaption = list[index]?.caption;
  const hasAnyCaption = list.some((it) => it.caption);

  // Reduced-motion preference (accessibility)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Page Visibility API
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => setIsTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // IntersectionObserver — only run timer when carousel is in view
  useEffect(() => {
    if (!canAutoplay || !rootRef.current || typeof IntersectionObserver === "undefined") return;
    const el = rootRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => setIsVisible(e.isIntersecting));
      },
      { threshold: 0.35 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [canAutoplay]);

  // One-time beckon nudge when the carousel first appears
  useEffect(() => {
    if (isVisible && !beckonPlayed && !prefersReducedMotion && canAutoplay) {
      setBeckonPlayed(true);
    }
  }, [isVisible, beckonPlayed, prefersReducedMotion, canAutoplay]);

  // Autoplay driver
  useEffect(() => {
    if (!canAutoplay) return undefined;
    const shouldRun =
      isVisible && !isHovered && !isTabHidden && !pausedByInteract && !prefersReducedMotion;
    if (!shouldRun) return undefined;
    autoplayTimerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, intervalMs);
    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    };
  }, [canAutoplay, isVisible, isHovered, isTabHidden, pausedByInteract, prefersReducedMotion, total, intervalMs]);

  // Cleanup interact-pause timer on unmount
  useEffect(
    () => () => {
      if (interactPauseTimerRef.current) clearTimeout(interactPauseTimerRef.current);
    },
    [],
  );

  const pauseForInteraction = () => {
    if (!canAutoplay) return;
    setPausedByInteract(true);
    if (interactPauseTimerRef.current) clearTimeout(interactPauseTimerRef.current);
    interactPauseTimerRef.current = setTimeout(() => {
      setPausedByInteract(false);
      interactPauseTimerRef.current = null;
    }, RESUME_AFTER_INTERACT_MS);
  };

  const prev = () => {
    setIndex((i) => (i - 1 + total) % total);
    pauseForInteraction();
  };
  const next = () => {
    setIndex((i) => (i + 1) % total);
    pauseForInteraction();
  };
  const jumpTo = (i) => {
    setIndex(i);
    pauseForInteraction();
  };

  const onKeyDown = (e) => {
    if (!canAutoplay) return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    }
  };

  // Circular arrow — responsive: 44px mobile, 48px tablet, 56px desktop
  const arrowBase =
    "group/arrow absolute top-1/2 -translate-y-1/2 z-20 flex items-center justify-center " +
    "h-11 w-11 md:h-12 md:w-12 lg:h-14 lg:w-14 rounded-full " +
    "bg-[rgba(10,10,10,0.55)] backdrop-blur-[6px] " +
    "border border-gold/40 " +
    "opacity-55 transition-all duration-200 ease-out " +
    "hover:opacity-100 hover:scale-[1.08] hover:border-gold " +
    "hover:shadow-[0_0_24px_rgba(200,164,106,0.35)] " +
    "active:scale-95 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-ink " +
    "focus-visible:opacity-100";

  return (
    <div
      className="w-full"
      ref={rootRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyDown={onKeyDown}
      tabIndex={-1}
    >
      {/* Stack — each image absolutely positioned, fades in when active */}
      <div className={`relative ${aspect} w-full overflow-visible`}>
        <div className="absolute inset-0 overflow-hidden">
          {visibleList.map((item, i) => (
            <div
              key={item.src + i}
              className="absolute inset-0 transition-opacity duration-[900ms] ease-luxe"
              style={{
                opacity: i === index ? 1 : 0,
                pointerEvents: i === index ? "auto" : "none",
                zIndex: i === index ? 2 : 1,
              }}
            >
              <Vitrine
                src={item.src}
                alt={`${alt} · ${i + 1}`}
                mode={item.mode}
                aspect={aspect}
                delay={i === 0 ? delay : 0}
                cleanOptions={item.cleanOptions}
                priority
              />
            </div>
          ))}
        </div>

        {/* Circular gold arrow nav — hidden in PDF export */}
        {total > 1 && !IS_EXPORT && (
          <>
            <button
              type="button"
              onClick={prev}
              data-testid="carousel-prev"
              aria-label="Previous slide"
              className={`${arrowBase} left-[-14px] md:left-[-20px] lg:left-[-24px]`}
            >
              <ChevronLeft
                className="h-4 w-4 md:h-5 md:w-5 lg:h-[22px] lg:w-[22px] text-gold transition-colors duration-200"
                strokeWidth={1.5}
              />
            </button>
            <button
              type="button"
              onClick={next}
              data-testid="carousel-next"
              aria-label="Next slide"
              className={`${arrowBase} right-[-14px] md:right-[-20px] lg:right-[-24px] ${
                beckonPlayed ? "animate-beckon" : ""
              }`}
            >
              <ChevronRight
                className="h-4 w-4 md:h-5 md:w-5 lg:h-[22px] lg:w-[22px] text-gold transition-colors duration-200 -mr-[1px]"
                strokeWidth={1.5}
              />
            </button>
          </>
        )}
      </div>

      {/* Dot indicators (hidden in PDF export) */}
      {total > 1 && !IS_EXPORT && (
        <div className="mt-4 flex items-center justify-center gap-3">
          {list.map((_, i) => (
            <button
              key={i}
              onClick={() => jumpTo(i)}
              data-testid={`vitrine-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className="group flex h-4 items-center justify-center"
            >
              <span
                className={`block transition-all duration-500 ease-luxe ${
                  i === index
                    ? "h-[3px] w-6 bg-gold"
                    : "h-[2px] w-3 bg-ivory/25 group-hover:bg-ivory/55"
                }`}
              />
            </button>
          ))}
        </div>
      )}

      {/* Active-slide caption — only rendered when any slide provides one */}
      {hasAnyCaption && (
        <div
          className="mt-3 min-h-[18px] text-center num-overline text-gold/85 text-[11px]"
          data-testid="vitrine-caption"
        >
          {activeCaption || ""}
        </div>
      )}
    </div>
  );
}
